import type { IBuffer } from '@xterm/headless';
import { TERMINAL_COLS, TERMINAL_ROWS } from '@api/remote/contracts';

/** The size a transcript recorded before sizes were (WRMM-171) is replayed at. */
const UNRECORDED_SIZE = { cols: 120, rows: 30 };
/**
 * The emulator's scrollback (each line costs about 12 bytes a column, so it stays modest in a browser tab), and how far
 * it may fill before the lines that have scrolled off are taken out into the text. Written in pieces of at most
 * WRITE_CHUNK characters (so at most that many lines each), lines are always taken before the emulator drops them,
 * whatever the transcript's length.
 */
const SCROLLBACK = 20_000, TAKE_AFTER = 10_000, WRITE_CHUNK = 8 * 1024;
/** Erases the emulator's scrollback: the replay's own, once those lines are taken (a device's is ignored, below). */
const ERASE_SCROLLBACK = '\u001b[3J';

/** What a transcript's screen part replays: what the device showed, and the terminal's size whenever it was set. */
export type ScreenPart = { text: string } | { cols: number; rows: number };
type Snapshot = { lines: string[]; wrapped: boolean };

/**
 * The text, built as lines are taken: each line without trailing spaces, at most one empty line in a row, none at the
 * start or end, in compact chunks. Its size follows the text, not the number of rows: a transcript of nothing but
 * line breaks stays small.
 */
class TextBuilder {
  private readonly chunks: string[] = [];
  private part = '';
  private line: string | null = null;
  private blank = false;
  /** A row of the screen: a new line, or (`wrapped`) the rest of the one before. */
  row(text: string, wrapped = false) {
    if (wrapped && this.line !== null) { this.line += text; return; }
    this.end();
    this.line = text;
  }
  private end() {
    if (this.line === null) return;
    const line = this.line.trimEnd();
    this.line = null;
    if (!line) { this.blank = true; return; }
    if (this.chunks.length || this.part) this.part += this.blank ? '\n\n' : '\n';
    this.part += line;
    this.blank = false;
    if (this.part.length > 64 * 1024) { this.chunks.push(this.part); this.part = ''; }
  }
  toString() { this.end(); return this.chunks.join('') + this.part; }
}

/**
 * What the device showed in a terminal session, as text the way the terminal displayed it (WRMM-171): the recorded
 * output replayed through a terminal emulator of the size the device drew for, so redraws (PSReadLine rewrites the
 * whole input line after each key), cursor moves and line breaks are applied rather than stripped. Nothing shown is
 * lost to a screen clear or a reset: what was on screen before one is kept, as is the last screen of a full-screen
 * program (tmux, an editor) when it ends. Lines a full-screen program scrolled away aren't recorded by any terminal and
 * aren't here.
 */
export async function screenText(parts: ScreenPart[]): Promise<string> {
  const { Terminal } = await import('@xterm/headless');
  const term = new Terminal({ ...UNRECORDED_SIZE, scrollback: SCROLLBACK, allowProposedApi: true });
  // Read afresh each time: a reset gives the emulator new buffers.
  const normal = () => term.buffer.normal;

  // The normal buffer's lines are taken into the text, in order, once they have scrolled off (they no longer change)
  // and then erased from the emulator, so buffer line y is always absolute line taken + y: nothing the emulator later
  // drops or rewraps on a resize was taken already. Screens kept from a clear go in before the line they preceded.
  const text = new TextBuilder();
  const kept: Array<({ at: number } & Snapshot) | undefined> = [];
  let keptHead = 0;
  const copyKept = () => {
    const snapshot = kept[keptHead]!;
    kept[keptHead++] = undefined; // Release the snapshot without moving the remaining queue.
    snapshot.lines.forEach((line, i) => text.row(line, i === 0 && snapshot.wrapped));
  };
  let taken = 0;
  const absolute = (y: number) => taken + y;
  const copy = (from: number, to: number) => {
    for (let y = from; y < to; y++, taken++) {
      while (keptHead < kept.length && kept[keptHead]!.at <= taken) copyKept();
      const line = normal().getLine(y);
      text.row(line?.translateToString(true) ?? '', !!line?.isWrapped);
    }
  };
  let erasing = false;
  /** Takes the normal buffer's scrollback out of the emulator. Only while it is the active buffer (ED 3 erases that). */
  const takeScrollback = async () => {
    if (term.buffer.active.type !== 'normal' || !normal().baseY) return;
    copy(0, normal().baseY);
    erasing = true;
    await write(ERASE_SCROLLBACK);
    erasing = false;
    term.options.scrollback = SCROLLBACK; // Back to size, if a resize during a full-screen program grew it.
  };

  /** Screens that would otherwise be lost, and the absolute line they belong before, in that order. */
  const keep = (at: number, snapshot: Snapshot) => {
    if (!snapshot.lines.length) return; // An empty screen cleared again: nothing to keep.
    // Almost always at the end: found from there, so clears repeated many times stay linear.
    let i = kept.length;
    while (i > keptHead && kept[i - 1]!.at > at) i--;
    kept.splice(i, 0, { at, ...snapshot });
  };
  const screen = (buffer: IBuffer, from: number): Snapshot => ({
    lines: trimEnd(joined(buffer, from, from + term.rows)),
    // A normal screen can start mid-line after its prefix scrolled off. Alternate screens belong between normal
    // lines, never joined to the prompt underneath them, even if their first row was wrapped by a full-screen app.
    wrapped: buffer.type === 'normal' && !!buffer.getLine(from)?.isWrapped,
  });
  term.parser.registerCsiHandler({ final: 'K' }, params => {
    const buffer = term.buffer.active, { cursorX: x, cursorY: y } = buffer;
    if ((params[0] ?? 0) !== 0 || x !== term.cols - 1) return false;
    // Erasing this space cannot change the display, but would lose the separator and xterm's wrapped-row metadata.
    return buffer.getLine(buffer.baseY + y)?.getCell(x)?.getChars() === ' ' && !!buffer.getLine(buffer.baseY + y + 1)?.isWrapped;
  });
  term.parser.registerCsiHandler({ final: 'J' }, params => {
    const mode = params[0] ?? 0, { cursorX: x, cursorY: y } = term.buffer.active;
    if (mode === 3) return !erasing; // A device clearing the scrollback: the transcript keeps it.
    // A clear of the whole screen, however it is written: all of it, from its start, or up to its last cell.
    const whole = mode === 2 || (mode === 0 && x === 0 && y === 0) || (mode === 1 && y === term.rows - 1 && x >= term.cols - 1);
    if (whole && term.buffer.active.type === 'normal') keep(absolute(normal().baseY), screen(normal(), normal().baseY));
    return false;
  });
  // A full reset (`reset`) clears both buffers, scrollback included: everything that showed is taken first (not the
  // screen's empty rows below it), then the reset runs, so what follows lands where it did on the screen.
  term.parser.registerEscHandler({ final: 'c' }, () => {
    if (term.buffer.active.type === 'alternate') keep(absolute(normal().baseY + normal().cursorY), screen(term.buffer.alternate, 0));
    let end = normal().length;
    while (end > 0 && !normal().getLine(end - 1)?.translateToString(true).trim()) end--;
    copy(0, end);
    return false;
  });
  term.parser.registerCsiHandler({ prefix: '?', final: 'l' }, params => {
    if (term.buffer.active.type === 'alternate' && params.some(p => p === 1049 || p === 1047 || p === 47)) {
      keep(absolute(normal().baseY + normal().cursorY), screen(term.buffer.alternate, 0));
    }
    return false;
  });

  const write = (data: string) => new Promise<void>(resolve => term.write(data, resolve));
  for (const part of parts) {
    if ('cols' in part) {
      // Taken before the resize, so a rewrap only ever moves lines not yet taken.
      await takeScrollback();
      const cols = clamp(part.cols, TERMINAL_COLS), rows = clamp(part.rows, TERMINAL_ROWS);
      // While a full-screen program runs the normal buffer can't be taken (ED 3 erases the active buffer), but it still
      // rewraps: room for all of it, until it is taken once the program ends.
      if (term.buffer.active.type === 'alternate') {
        term.options.scrollback = Math.max(term.options.scrollback ?? SCROLLBACK, (normal().length + rows) * Math.ceil(term.cols / cols));
      }
      term.resize(cols, rows);
      continue;
    }
    for (let at = 0; at < part.text.length; at += WRITE_CHUNK) {
      await write(part.text.slice(at, at + WRITE_CHUNK));
      if (normal().baseY > TAKE_AFTER) await takeScrollback();
    }
  }
  if (term.buffer.active.type === 'alternate') keep(absolute(normal().baseY + normal().cursorY), screen(term.buffer.alternate, 0));
  copy(0, normal().length);
  while (keptHead < kept.length) copyKept();
  term.dispose();
  return text.toString();
}

function joined(buffer: IBuffer, from: number, to: number) {
  const lines: string[] = [];
  for (let y = from; y < Math.min(to, buffer.length); y++) {
    const line = buffer.getLine(y);
    if (!line) continue;
    const text = line.translateToString(true);
    if (line.isWrapped && lines.length) lines[lines.length - 1] += text; else lines.push(text);
  }
  return lines;
}
/** Without trailing spaces on each line, or empty lines at the end. */
const trimEnd = (lines: string[]) => { const out = lines.map(l => l.trimEnd()); while (out.length && !out.at(-1)!.trim()) out.pop(); return out; };
const clamp = (n: number, { min, max }: { min: number; max: number }) => Number.isFinite(n) ? Math.min(max, Math.max(min, Math.round(n))) : min;
